import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';
import { id } from 'date-fns/locale';
import { DateRangePreset, PerformanceStatus, PerformanceDistributionData } from './types';
import { trackDownload } from '../activity';

// Status color mappings
export const getStatusColor = (status: PerformanceStatus): string => {
    switch (status) {
        case 'excellent': return 'green';
        case 'good': return 'blue';
        case 'average': return 'yellow';
        case 'poor': return 'red';
        default: return 'gray';
    }
};

// Get status badge variant
export const getStatusVariant = (status: PerformanceStatus): 'solid' | 'outline' => {
    return status === 'excellent' || status === 'poor' ? 'solid' : 'outline';
};

// Predefined date ranges
export const getPredefinedDateRange = (range: DateRangePreset) => {
    const today = new Date();
    
    if (range === 'this_week') {
        return {
            start: format(startOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
            end: format(endOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
            label: 'Minggu Ini'
        };
    }
    
    if (range === 'this_month') {
        return {
            start: format(startOfMonth(today), 'yyyy-MM-dd'),
            end: format(endOfMonth(today), 'yyyy-MM-dd'),
            label: 'Bulan Ini'
        };
    }
    
    if (range === 'last_7_days') {
        return {
            start: format(subDays(today, 6), 'yyyy-MM-dd'),
            end: format(today, 'yyyy-MM-dd'),
            label: '7 Hari Terakhir'
        };
    }
    
    if (range === 'last_30_days') {
        return {
            start: format(subDays(today, 29), 'yyyy-MM-dd'),
            end: format(today, 'yyyy-MM-dd'),
            label: '30 Hari Terakhir'
        };
    }
    
    // Default to this week for 'custom' and any other values
    return {
        start: format(startOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
        end: format(endOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
        label: 'Minggu Ini'
    };
};

// Format date for display
export const formatDisplayDate = (dateString: string): string => {
    try {
        return format(new Date(dateString), 'dd MMM yyyy', { locale: id });
    } catch {
        return dateString;
    }
};

// Calculate percentage
export const calculatePercentage = (value: number, total: number): number => {
    if (total === 0) return 0;
    return (value / total) * 100;
};

// Format percentage for display
export const formatPercentage = (value: number, decimals: number = 1): string => {
    return `${value.toFixed(decimals)}%`;
};

// Generate performance distribution data for charts
export const generatePerformanceDistributionData = (summary: {
    excellent_count: number;
    good_count: number;
    average_count: number;
    poor_count: number;
}): PerformanceDistributionData[] => {
    return [
        { name: 'Excellent', value: summary.excellent_count, color: '#38A169' },
        { name: 'Good', value: summary.good_count, color: '#3182CE' },
        { name: 'Average', value: summary.average_count, color: '#D69E2E' },
        { name: 'Poor', value: summary.poor_count, color: '#E53E3E' },
    ].filter(item => item.value > 0);
};

// Compare value with benchmark
export const compareWithBenchmark = (
    value: number, 
    benchmark: number, 
    higherIsBetter: boolean = true
): 'above' | 'below' | 'equal' => {
    const tolerance = 0.01; // 0.01% tolerance for equality
    
    if (Math.abs(value - benchmark) <= tolerance) return 'equal';
    
    if (higherIsBetter) {
        return value > benchmark ? 'above' : 'below';
    } else {
        return value < benchmark ? 'above' : 'below';
    }
};

// Get comparison color
export const getComparisonColor = (
    value: number, 
    benchmark: number, 
    higherIsBetter: boolean = true
): string => {
    const comparison = compareWithBenchmark(value, benchmark, higherIsBetter);
    
    switch (comparison) {
        case 'above': return 'green.600';
        case 'below': return 'red.600';
        case 'equal': return 'blue.600';
        default: return 'gray.600';
    }
};

// Performance score ranges
export const getScoreRange = (score: number): {
    label: string;
    color: string;
    description: string;
} => {
    if (score >= 80) {
        return {
            label: 'Excellent',
            color: 'green',
            description: 'Kinerja sangat baik, layak mendapat bonus'
        };
    } else if (score >= 65) {
        return {
            label: 'Good',
            color: 'blue',
            description: 'Kinerja baik, perlu sedikit perbaikan'
        };
    } else if (score >= 50) {
        return {
            label: 'Average',
            color: 'yellow',
            description: 'Kinerja rata-rata, perlu peningkatan'
        };
    } else {
        return {
            label: 'Poor',
            color: 'red',
            description: 'Kinerja buruk, perlu pelatihan intensif'
        };
    }
};

// Generate recommendations based on metrics
export const generateRecommendations = (harvester: {
    rasio_kematangan: number;
    rasio_buah_mentah: number;
    rasio_buah_busuk: number;
    rasio_tangkai_panjang: number;
    rerata_brondolan: number;
    produktivitas_harian: number;
    total_score: number;
}, benchmark: {
    avg_rasio_kematangan: number;
    avg_rasio_buah_mentah: number;
    avg_rasio_buah_busuk: number;
    avg_rasio_tangkai_panjang: number;
    avg_rerata_brondolan: number;
    avg_produktivitas_harian: number;
}): string[] => {
    const recommendations: string[] = [];

    // Score-based recommendations
    if (harvester.total_score >= 80) {
        recommendations.push('Layak mendapat bonus/insentif');
        recommendations.push('Jadikan mentor untuk pemanen lain');
    } else if (harvester.total_score < 50) {
        recommendations.push('Perlu pelatihan intensif');
        recommendations.push('Monitoring harian diperlukan');
    }

    // Specific metric recommendations
    if (harvester.rasio_kematangan < benchmark.avg_rasio_kematangan - 5) {
        recommendations.push('Pelatihan kriteria buah matang');
    }

    if (harvester.rasio_buah_mentah > benchmark.avg_rasio_buah_mentah + 2) {
        recommendations.push('Review jadwal dan teknik panen');
    }

    if (harvester.rasio_tangkai_panjang > benchmark.avg_rasio_tangkai_panjang + 2) {
        recommendations.push('Pelatihan teknik pemotongan');
    }

    if (harvester.rerata_brondolan < benchmark.avg_rerata_brondolan - 5) {
        recommendations.push('Pelatihan pengumpulan brondolan');
    }

    if (harvester.produktivitas_harian < benchmark.avg_produktivitas_harian - 10) {
        recommendations.push('Review target dan motivasi');
    }

    return recommendations.length > 0 ? recommendations : ['Pertahankan kinerja saat ini'];
};

// Export data to CSV format
export const exportToCSV = (data: any[], filename: string = 'harvester_performance'): void => {
    if (!data || data.length === 0) return;

    const headers = Object.keys(data[0]);
    const csvContent = [
        headers.join(','),
        ...data.map(row => 
            headers.map(header => {
                const value = row[header];
                // Handle values that might contain commas
                if (typeof value === 'string' && value.includes(',')) {
                    return `"${value}"`;
                }
                return value;
            }).join(',')
        )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `${filename}_${format(new Date(), 'yyyy-MM-dd')}.csv`);
        trackDownload('Harvesters Performance', { filename });
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
};

// Validate date range
export const validateDateRange = (startDate: string, endDate: string): {
    isValid: boolean;
    error?: string;
} => {
    if (!startDate || !endDate) {
        return { isValid: false, error: 'Tanggal mulai dan akhir harus diisi' };
    }

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (start > end) {
        return { isValid: false, error: 'Tanggal mulai tidak boleh lebih besar dari tanggal akhir' };
    }

    const diffDays = (end.getTime() - start.getTime()) / (1000 * 3600 * 24);
    if (diffDays > 365) {
        return { isValid: false, error: 'Rentang tanggal tidak boleh lebih dari 1 tahun' };
    }

    return { isValid: true };
};

// Get metric trend indicator
export const getTrendIndicator = (current: number, previous: number): {
    direction: 'up' | 'down' | 'stable';
    percentage: number;
    color: string;
} => {
    const tolerance = 0.1; // 0.1% tolerance for stable
    const change = ((current - previous) / previous) * 100;

    if (Math.abs(change) <= tolerance) {
        return { direction: 'stable', percentage: 0, color: 'gray.500' };
    }

    return {
        direction: change > 0 ? 'up' : 'down',
        percentage: Math.abs(change),
        color: change > 0 ? 'green.500' : 'red.500'
    };
};
